import React, { useEffect, useMemo } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import UserService from '../../../services/UserService';
import { Pie } from '@ant-design/charts';
import IconNoData from '../../../assets/Images/icons/icon-no-data';

export default function Dataflow({ summary }) {
    const { t } = useTranslation();
    const [dataPie, setDataPie] = useState([]);

    useEffect(() => {
        setDataPie([
            {
                type: t('Draft'),
                value: summary?.dataFlowDraft || 0,
            },
            {
                type: t('Waiting for Review'),
                value: summary?.dataFlowWaitingForReview || 0,
            },
            {
                type: t('Reviewed'),
                value: summary?.dataFlowReviewed || 0,
            },
            {
                type: t('Revise'),
                value: summary?.dataFlowRevise || 0,
            },
        ]);
    }, [summary, t]);

    // Role
    const role = UserService.role().toLowerCase();
    const admin = ['admin', 'auditor', 'super admin'].includes(role);
    const totalValue = dataPie.reduce((acc, item) => acc + item.value, 0);

    const config = useMemo(() => {
        return {
            data: dataPie,
            angleField: 'value',
            colorField: 'type',
            color: ({ type }) => {
                switch (type) {
                    case t('Draft'):
                        return '#9CE2DD';
                    case t('Waiting for Review'):
                        return '#61DDAA';
                    case t('Reviewed'):
                        return '#44B6A5';
                    case t('Revise'):
                        return '#BAC2F2';
                    default:
                        return '#9CE2DD';
                }
            },
            width: 200,
            height: 200,
            radius: 1,
            innerRadius: 0.6,
            statistic: {
                title: {
                    content: t('Total'),
                    style: {
                        fontSize: '12px',
                    },
                },
                content: {
                    content: totalValue > 0 ? totalValue.toString() : '0',
                },
            },
        };
    }, [dataPie, t, totalValue]);

    const dataFlowDraftPercent = (
        (summary?.dataFlowDraft / summary?.dataFlowTotal) *
        100
    ).toFixed(2);

    const dataFlowWaitingForReviewPercent = (
        (summary?.dataFlowWaitingForReview / summary?.dataFlowTotal) *
        100
    ).toFixed(2);

    const dataFlowReviewedPercent = (
        (summary?.dataFlowReviewed / summary?.dataFlowTotal) *
        100
    ).toFixed(2);

    const dataFlowRevisePercent = (
        (summary?.dataFlowRevise / summary?.dataFlowTotal) *
        100
    ).toFixed(2);

    return (
        <div>
            <div className="flex space-x-2 items-center pb-4 justify-between">
                <div className="font-semibold text-xl">
                    {admin
                        ? t('Data Flow in your organization')
                        : t('Data Flow in your department')}
                </div>
            </div>
            {summary ? (
                <>
                    <Pie {...config} />
                    <div className="flex justify-between pt-4 pb-2 ">
                        <div className="flex items-center">
                            <Icon color="#9CE2DD" />
                            <div className="pl-2">{t('Draft')}</div>
                        </div>
                        <div className="text-gray-500">
                            {summary?.dataFlowDraft || 0} (
                            {isNaN(dataFlowDraftPercent)
                                ? 0
                                : dataFlowDraftPercent}
                            %)
                        </div>
                    </div>

                    <div className="flex justify-between pb-2">
                        <div className="flex items-center">
                            <Icon color="#61DDAA" />
                            <div className="pl-2">
                                {t('Waiting for Review')}
                            </div>
                        </div>
                        <div className="text-gray-500">
                            {summary?.dataFlowWaitingForReview || 0} (
                            {isNaN(dataFlowWaitingForReviewPercent)
                                ? 0
                                : dataFlowWaitingForReviewPercent}
                            %)
                        </div>
                    </div>

                    <div className="flex justify-between pb-2">
                        <div className="flex items-center">
                            <Icon color="#44B6A5" />
                            <div className="pl-2">{t('Reviewed')}</div>
                        </div>
                        <div className="text-gray-500">
                            {summary?.dataFlowReviewed || 0} (
                            {isNaN(dataFlowReviewedPercent)
                                ? 0
                                : dataFlowReviewedPercent}
                            %)
                        </div>
                    </div>

                    <div className="flex justify-between pb-2">
                        <div className="flex items-center">
                            <Icon color="#BAC2F2" />
                            <div className="pl-2">{t('Revise')}</div>
                        </div>
                        <div className="text-gray-500">
                            {summary?.dataFlowRevise || 0} (
                            {isNaN(dataFlowRevisePercent)
                                ? 0
                                : dataFlowRevisePercent}
                            %)
                        </div>
                    </div>

                    <div
                        className="pt-2 flex justify-between"
                        style={{ borderTop: '0.5px solid #E9EBF1' }}
                    >
                        <div className="font-semibold">{t('Total')}</div>
                        <div className="text-gray-500">
                            {summary?.dataFlowTotal} (100%)
                        </div>
                    </div>
                </>
            ) : (
                <div className="text-center text-sm space-y-2 my-32">
                    <IconNoData />
                    <p className="text-[#65707A] text-[14px]">
                        {t('common.noData')}
                    </p>
                </div>
            )}
        </div>
    );
}

const Icon = ({ color }) => (
    <svg
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <circle cx="6" cy="6" r="6" fill={color} />
    </svg>
);
